import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { copyText } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Mail, Copy, Clock, X, ArrowLeft } from "lucide-react";

const ROLE_LABEL = { agency_admin: "Agency Admin", client_admin: "Client Admin", client_user: "Mitarbeiter", platform_admin: "Platform Admin" };
const STATUS_LABEL = {
  pending: { label: "Ausstehend", className: "bg-amber-100 text-amber-800" },
  accepted: { label: "Angenommen", className: "bg-emerald-100 text-emerald-800" },
  expired: { label: "Abgelaufen", className: "bg-slate-200 text-slate-700" },
  revoked: { label: "Widerrufen", className: "bg-red-100 text-red-700" },
};

export default function ClientUsers() {
  const { client_id } = useParams();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "client_user" });
  const [lastInviteLink, setLastInviteLink] = useState("");
  const inviteLinkInputRef = useRef(null);

  const copyLink = async (link) => {
    const ok = await copyText(link, inviteLinkInputRef.current);
    if (ok) toast.success("Link kopiert");
    else {
      // Letzter Ausweg: Input markieren, damit der User mit Strg+C manuell kopiert
      try { inviteLinkInputRef.current?.focus(); inviteLinkInputRef.current?.select(); } catch (_) { /* noop */ }
      toast.error("Bitte mit Strg+C / Cmd+C kopieren (Link ist markiert)", { duration: 5000 });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i, clients] = await Promise.all([
        api.get(`/agency/clients/${client_id}/users`),
        api.get(`/agency/clients/${client_id}/invites`),
        api.get(`/agency/clients`),
      ]);
      setUsers(u.data.users || []);
      setInvites(i.data.invites || []);
      const c = (clients.data.clients || []).find(x => x.company_id === client_id);
      if (c) setClientName(c.company_name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  }, [client_id]);

  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("Gültige E-Mail erforderlich"); return; }
    setCreating(true);
    try {
      const r = await api.post(`/agency/clients/${client_id}/invites`, form);
      const link = `${window.location.origin}${r.data.invite_path}`;
      setLastInviteLink(link);
      toast.success("Einladung erstellt");
      setForm({ name: "", email: "", role: "client_user" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setCreating(false); }
  };

  const buildLink = (token) => `${window.location.origin}/invite/${token}`;

  const revoke = async (invite_id) => {
    try {
      await api.post(`/agency/clients/${client_id}/invites/${invite_id}/revoke`);
      toast.success("Einladung widerrufen");
      await load();
    } catch (e) { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="client-users-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => nav("/agency/clients")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Zurück zu Mandanten
          </button>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Nutzer von {clientName || "…"}</h1>
          <p className="text-sm text-slate-500 mt-1">Verwalten Sie wer Zugriff auf diesen Mandanten hat.</p>
        </div>
        <Button data-testid="invite-user-btn" onClick={() => { setLastInviteLink(""); setInviteOpen(true); }} className="bg-orange-600 hover:bg-orange-700 text-white">
          <UserPlus className="w-4 h-4 mr-2" /> Nutzer einladen
        </Button>
      </header>

      <Card className="border-slate-200" data-testid="users-list">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-700">Aktive Nutzer ({users.length})</div>
        {loading ? <div className="p-6 text-slate-500">Wird geladen …</div> : users.length === 0 ? (
          <div className="p-6 text-slate-500">Noch keine Nutzer.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {users.map(u => (
              <li key={u.user_id} data-testid={`user-row-${u.user_id}`} className="px-5 py-3 flex items-center gap-3">
                {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">{(u.name || u.email)[0]?.toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{u.name || u.email}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <Badge variant="outline" className="text-xs">{ROLE_LABEL[u.role] || u.role}</Badge>
                <Badge className={u.status === "active" ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-300 text-slate-700"}>{u.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="border-slate-200" data-testid="invites-list">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slate-700 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Einladungen ({invites.length})
        </div>
        {invites.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">Noch keine Einladungen verschickt.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invites.map(inv => {
              const meta = STATUS_LABEL[inv.status] || STATUS_LABEL.pending;
              return (
                <li key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`} className="px-5 py-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{inv.name || inv.email}</p>
                    <p className="text-xs text-slate-500 truncate">{inv.email} · {ROLE_LABEL[inv.role] || inv.role}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${meta.className}`}>{meta.label}</span>
                  {inv.status === "pending" && (
                    <>
                      <Button data-testid={`copy-invite-${inv.invite_id}`} variant="outline" size="sm" onClick={() => copyLink(buildLink(inv.token))}>
                        <Copy className="w-3 h-3 mr-1" /> Link
                      </Button>
                      <Button data-testid={`revoke-invite-${inv.invite_id}`} variant="ghost" size="sm" onClick={() => revoke(inv.invite_id)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(inv.created_at).toLocaleDateString("de-DE")}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="invite-dialog" className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-orange-600" />
              {lastInviteLink ? "Einladung erstellt" : "Nutzer einladen"}
            </DialogTitle>
            <DialogDescription>
              {lastInviteLink
                ? "Senden Sie diesen Link an den Eingeladenen. Er meldet sich danach mit Google an."
                : "Senden Sie eine Einladung. Der Eingeladene meldet sich danach mit Google an."}
            </DialogDescription>
          </DialogHeader>

          {lastInviteLink ? (
            <div className="space-y-4" data-testid="invite-link-block">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5">Einladungs-Link</p>
                <div className="flex items-stretch gap-2">
                  <Input
                    ref={inviteLinkInputRef}
                    data-testid="invite-link-value"
                    readOnly
                    value={lastInviteLink}
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.target.select()}
                    className="font-mono text-xs bg-white"
                  />
                  <Button
                    size="sm"
                    type="button"
                    data-testid="copy-last-invite"
                    onClick={() => copyLink(lastInviteLink)}
                    className="bg-slate-900 hover:bg-slate-800 text-white whitespace-nowrap"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Kopieren
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">Tipp: Versenden Sie den Link per E-Mail, WhatsApp oder Slack — der Eingeladene wird beim ersten Klick automatisch mit dem richtigen Mandanten verknüpft.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-600">Name</Label>
                <Input
                  data-testid="invite-name-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Max Mustermann"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-600">E-Mail *</Label>
                <Input
                  data-testid="invite-email-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="max@firma.de"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-600">Rolle</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_admin">Client Admin (volle Mandantenrechte)</SelectItem>
                    <SelectItem value="client_user">Mitarbeiter (Standardrechte)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {!lastInviteLink && (
              <>
                <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={creating}>Abbrechen</Button>
                <Button
                  data-testid="submit-invite-btn"
                  onClick={createInvite}
                  disabled={creating || !form.email.trim()}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {creating ? "Erstelle …" : "Einladung erstellen"}
                </Button>
              </>
            )}
            {lastInviteLink && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setLastInviteLink(""); setForm({ name: "", email: "", role: "client_user" }); }}
                  data-testid="invite-another-btn"
                >
                  Weitere Einladung
                </Button>
                <Button
                  onClick={() => setInviteOpen(false)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="invite-done-btn"
                >
                  Fertig
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
